import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  TableContainer,
  Table,
  Tr,
  Grid,
  GridItem,
  Tbody,
  Td,
  TableCaption,
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import * as _ from 'lodash';

import {
  DistrictOutput,
  DistrictDemoData,
  PricingQuotePriceOutput,
  QuoteV2Get,
  UniverseType,
  ClientType,
  ClientBudget,
  ClientComplexity,
  ResearchComplexity,
} from '../../lib/pricing-tool-api';
import {
  IQuoteService,
  useQuoteService,
} from '../../lib/services/QuoteService';
import { LocationInformationTable } from '../../components/pricing/LocationInformationSection';
import {
  RecruitmentCostSection,
  GenerateQuoteSection,
  QuoteListSection,
  ProposalListSection,
} from '../../components/pricing/generateQuoteSection';
import {
  // OVERSAMPLE_OPTIONS,
  UNIVERSE_TYPE_OPTIONS,
} from '../../components/pricing/TargetUniverseSection';
import { formatManualMarkupForDisplay } from '../../components/pricing/FeeTypeSection';
import { calculateMaxRecommendedSampleSize } from '../../hooks/pricing/usePriceQuote';

// For now, using same types as v1 since API structure will be similar
// We'll update these when the v2 API types are generated
interface SimpleDetailTableProps {
  data: Map<string, string | undefined>;
  title: string;
}

function SimpleDetailTable(props: SimpleDetailTableProps) {
  const { data, title } = props;
  return (
    <TableContainer>
      <Table size="sm">
        <TableCaption placement="top" bg="brand.primaryColor">
          {title}
        </TableCaption>
        <Tbody>
          {[...data.keys()].map((key) => (
            <Tr key={key}>
              <Td>{key}</Td>
              <Td whiteSpace="normal" overflow="auto">
                {data.get(key)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

interface QuoteV2DescriptionProps {
  quote: QuoteV2Get | undefined;
}

function QuoteV2Description(props: QuoteV2DescriptionProps) {
  const { quote } = props;
  const navigate = useNavigate();

  const [districtDemoData] = useState<DistrictDemoData[]>(
    quote?.district_demo_data && quote?.district_demo_data.length > 0
      ? quote?.district_demo_data
      : [],
  );

  // eslint-disable-next-line arrow-body-style
  const clientInformation = () => {
    // Keeping this here in case we need to restore exception criteria logic
    // let suggestedTier = 'Medium';
    // if (quote?.price_info.request.exception_criteria !== 'none') {
    //   suggestedTier = 'Low';
    // }
    const budgetReason = (quote?.price_info.request as any)
      ?.client_budget_reason as string | undefined;
    const rows: Array<[string, string | undefined]> = [
      ['Client Type', quote?.price_info.request.client_type],
      ['Client Budget', quote?.price_info.request.client_budget],
      ['Client Complexity', quote?.price_info.request.client_complexity],
      [
        'Business Unit',
        (() => {
          const businessUnit = (quote?.price_info.request as any)
            ?.business_unit;
          if (!businessUnit) return '';
          return businessUnit === 'pna' ? 'PNA' : 'Embold';
        })(),
      ],
    ];
    if (budgetReason) {
      rows.push(['Budget Note', budgetReason]);
    }
    return new Map<string, string | undefined>(rows);
  };

  const pollInformation = () =>
    new Map<string, string | undefined>([
      ['Research Complexity', quote?.price_info.request.research_complexity],
      [
        'Video Length (seconds)',
        quote?.price_info.request.video_length?.toString(),
      ],
      [
        'Creative Testing',
        quote?.price_info.request.creative_testing ? 'Yes' : 'No',
      ],
      [
        'Low Bandwidth',
        (quote?.price_info.request as any)?.low_bandwidth ? 'Yes' : 'No',
      ],
      // Add new V2 specific parameters here when available
    ]);

  const targetUniverseInfo = () =>
    // let oversampleType = quote?.price_info.request.oversample_type?.toString();
    // if (oversampleType) {
    //   // const mappedOversampleType = OVERSAMPLE_OPTIONS.find(
    //   //   (option) => option.value === oversampleType,
    //   // )?.choiceText;
    //   // if (mappedOversampleType) {
    //   //   oversampleType = mappedOversampleType;
    //   // }
    // }
    new Map<string, string | undefined>([
      [
        'Additional Crosstabs',
        quote?.price_info.request.num_of_additional_crosstabs.toString(),
      ],
      [
        'Additional Weighting Universes',
        quote?.price_info.request.num_of_additional_weighting_universes.toString(),
      ],
      [
        'Additional Monitoring Universes',
        quote?.price_info.request.num_of_additional_monitoring_universes.toString(),
      ],
      [
        'Number of Oversamples',
        quote?.price_info.request.number_of_oversamples
          ? quote?.price_info.request.number_of_oversamples.toString()
          : '0',
      ],
      [
        'Universe Types',
        quote?.price_info.request.universe_types
          .map((u) => {
            const universeType = UNIVERSE_TYPE_OPTIONS.find(
              (ut) => ut.value === u,
            );
            return universeType ? universeType.choiceText : u.toString();
          })
          .join(', '),
      ],
      // ['Oversample Type', oversampleType],
      [
        'Special Demographic Filtering',
        `${(quote?.price_info.request.special_demo_filter_pct || 0) * 100}%`,
      ],
    ]);

  const feeTypeInfo = () => {
    const markup = formatManualMarkupForDisplay(
      quote?.price_info.request.markup_or_discount_percentage || 1.0,
    );
    const additionalFees = quote?.price_info.request.absolute_markup_or_discount || 0;
    
    return new Map<string, string | undefined>([
      ['Mark-up (+) / Discount (-) Percentage', `${markup}%`],
      ['Additional fee(s)', `$${additionalFees}`],
    ]);
  };

  const maxRecommendedSampleSize = () =>
    districtDemoData.length === 0
      ? Number.MAX_SAFE_INTEGER // this is a hack so that the table doesn't show a warning if demo data is missing
      : calculateMaxRecommendedSampleSize(
          districtDemoData,
          (quote?.price_info.request.universe_types as UniverseType[]) || [],
        );

  const demoDataAsMap = () => {
    const result = new Map<string, DistrictDemoData>();
    districtDemoData.forEach((demo) => {
      const id =
        demo.district.id ||
        `${demo.district.state}|${demo.district.district_type}|${demo.district.name}`;
      result.set(id, demo);
    });

    return result;
  };

  const onEditButtonClick = () => {
    // For now, navigate to new quote page (since V2 edit isn't implemented yet)
    navigate(`/pricing/quote?id=${quote?.id}`, { replace: true });
  };

  return (
    <Box>
      <Text fontSize="2xl" fontWeight="bold" mb={4}>
        New Quote (V2) - {quote?.deal_name}
      </Text>

      <Grid templateColumns="repeat(3, 1fr)" gap="20px">
        <GridItem colSpan={3}>
          <Button
            float="right"
            leftIcon={<EditIcon />}
            variant="solid"
            colorScheme="blue"
            onClick={onEditButtonClick}
          >
            Edit
          </Button>
        </GridItem>
        <GridItem fontSize="md">
          <SimpleDetailTable
            data={clientInformation()}
            title="Client Information"
          />
        </GridItem>
        <GridItem fontSize="md">
          <SimpleDetailTable
            data={pollInformation()}
            title="Poll Information"
          />
        </GridItem>
        <GridItem fontSize="md">
          <SimpleDetailTable
            data={targetUniverseInfo()}
            title="Target Universe"
          />
        </GridItem>

        <GridItem fontSize="md" colSpan={3}>
          <SimpleDetailTable data={feeTypeInfo()} title="Fee Type" />
        </GridItem>
        <GridItem colSpan={3}>
          <Text bg="brand.primaryColor" fontSize="md">
            Location Information
          </Text>
          <LocationInformationTable
            selectedDistricts={
              quote?.price_info.request.districts as DistrictOutput[]
            }
            selectedDistrictsDemoData={demoDataAsMap()}
            onRemoveDistrict={() => {}}
            showRemoveButton={false}
            allowNullDemoData
            size="sm"
          />
        </GridItem>
      </Grid>

      <Text bg="brand.primaryColor" mb={4}>
        Prices
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap="20px">
        <GridItem>
          <RecruitmentCostSection
            calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize()}
            quotes={quote?.price_info.prices as PricingQuotePriceOutput[]}
            numberOfQuestions={
              quote?.price_info.request.number_of_questions as number[]
            }
            sampleSizes={quote?.price_info.request.sample_sizes as number[]}
          />
        </GridItem>

        <GridItem>
          <QuoteListSection
            calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize()}
            quotes={quote?.price_info.prices as PricingQuotePriceOutput[]}
            numberOfQuestions={
              quote?.price_info.request.number_of_questions as number[]
            }
            sampleSizes={quote?.price_info.request.sample_sizes as number[]}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <ProposalListSection
            calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize()}
            quotes={quote?.price_info.prices as PricingQuotePriceOutput[]}
            clientType={
              quote?.price_info.request.client_type || ClientType.Downballot
            }
            clientBudget={
              quote?.price_info.request.client_budget || ClientBudget.Medium
            }
            clientComplexity={
              quote?.price_info.request.client_complexity ||
              ClientComplexity.Standard
            }
            researchComplexity={
              quote?.price_info.request.research_complexity ||
              ResearchComplexity.Standard
            }
            universeTypes={
              quote?.price_info.request.universe_types as UniverseType[]
            }
            numberOfQuestions={
              quote?.price_info.request.number_of_questions as number[]
            }
            sampleSizes={quote?.price_info.request.sample_sizes as number[]}
          />
        </GridItem>
      </Grid>
    </Box>
  );
}

function QuoteDetailV2() {
  const { quoteId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [quote, setQuote] = useState<QuoteV2Get>();
  const [errMsg, setErrMsg] = useState<string>();
  const quoteService = useQuoteService();

  const fetchSavedQuoteV2 = async (api: IQuoteService, id: string) => {
    const response = await api.getQuoteV2(id, true);
    return response;
  };

  useEffect(() => {
    if (!quoteId) {
      setErrMsg('Quote ID is required');
      setIsLoading(false);
      return;
    }

    fetchSavedQuoteV2(quoteService, quoteId)
      .then((response) => {
        setQuote(response.data);
        setIsLoading(false);
      })
      .catch(() => {
        setErrMsg(
          `Could not fetch quote with ID ${quoteId}. Please contact the tech team with any questions.`,
        );
        setIsLoading(false);
      });
  }, [quoteId, quoteService]);

  return (
    <Box>
      {isLoading ? (
        <Spinner />
      ) : (
        <Box>
          {!errMsg ? (
            <QuoteV2Description quote={quote} />
          ) : (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Fetch Error</AlertTitle>
              <AlertDescription>{errMsg}</AlertDescription>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}

export default QuoteDetailV2;
